import type { Card } from '../../../../card/domain/Card'
import type { CardDTO } from '../../../../card/domain/CardDTO'
import type { CommonItem, CommonItemChildren } from '../../../domain/CommonItem'
import type { CommonItemDTO, CommonItemDTOChild } from '../../../domain/CommonItemDTO'
import type { ContentMedia } from '../../../../content-media/domain/ContentMedia'
import type { ContentMediaDTO } from '../../../../content-media/domain/ContentMediaDTO'
import { ContentModelId } from '../../../../../types/ContentModelId'
import type { ContentPage } from '../../../../content-page/domain/ContentPage'
import type { ContentPageDTO } from '../../../../content-page/domain/ContentPageDTO'
import type { PartnerId } from '../../../../../types/PartnerId'

import { isAvailablePartner } from '../../../../../utils/filter-by-partner'
import { isNonNullableEntryDTO } from '../../../../../utils/non-nullable-entry-dto'

import { mapEntryDTOList } from '../../../../../utils/map-entry-dto-list'
import { mapContentMediaFromApi } from '../../../../content-media/infrastructure/repository/mapper/content-media.mapper'

const isChildContentModel = (dto: CommonItemDTOChild, contentModel: ContentModelId): boolean =>
  dto?.sys.contentType.sys.id === contentModel
const isChildContentModelList = <T extends CommonItemDTOChild>(
  entries: Array<CommonItemDTOChild>,
  contentModel: ContentModelId,
): entries is Array<NonNullable<T>> =>
  entries.every((child: CommonItemDTOChild) => isChildContentModel(child, contentModel))

export const mapCommonItemFromApi = (dto: CommonItemDTO, partnerId?: PartnerId): CommonItem | undefined => {
  if (!(isNonNullableEntryDTO(dto) && isAvailablePartner(partnerId, dto.fields.partnerFilter))) {
    return undefined
  }

  const {
    anchorLabel,
    children: childrenDto,
    displayType,
    description,
    linkLabel,
    linkUrl,
    media,
    skinVariant,
    title,
  } = dto.fields

  let children: CommonItemChildren | undefined

  if (childrenDto?.length) {
    const nonNullableChildren = childrenDto.filter(isNonNullableEntryDTO)

    if (isChildContentModelList<CardDTO>(nonNullableChildren, ContentModelId.CARD)) {
      children = mapEntryDTOList<Card>(nonNullableChildren)
    } else if (isChildContentModelList<CommonItemDTO>(nonNullableChildren, ContentModelId.COMMON_ITEM)) {
      children = mapEntryDTOList<CommonItem>(nonNullableChildren, partnerId)
    } else if (isChildContentModelList<ContentMediaDTO>(nonNullableChildren, ContentModelId.CONTENT_MEDIA)) {
      children = mapEntryDTOList<ContentMedia>(nonNullableChildren, partnerId)
    } else if (isChildContentModelList<ContentPageDTO>(nonNullableChildren, ContentModelId.CONTENT_PAGE)) {
      children = mapEntryDTOList<ContentPage>(nonNullableChildren, partnerId)
    }
  }

  return {
    anchorLabel,
    contentType: ContentModelId.COMMON_ITEM,
    children,
    description,
    displayType,
    linkLabel,
    linkUrl,
    media: mapContentMediaFromApi(media),
    skinVariant,
    title,
  }
}
